import { is } from '@client/helpers/dom.guards';
import { hasOverlayParent } from '@client/helpers/dom.helpers';
import { shouldFillEmptyFieldOnArrowRight } from '@client/helpers/empty-field-fill.helpers';
import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { useElementExpressionVariablesById } from '@client/hooks/use-element-expression-variables.hooks';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { SidePanelStyles } from '@client/style/style.constants';
import { Box, Drawer } from '@mui/material';
import { Button } from '@ui/buttons';
import {
  fixNumberFormatSolve,
  ISolveOptions,
} from '@shared/helpers/expression-solving.helpers';
import { ExpressionVariables } from '@shared/helpers/expression-variables.helpers';
import { sleep } from '@shared/helpers/promise.helpers';
import { isSelectableQuantityUnit } from '@shared/helpers/unit.helpers';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { ExpressionValue, IElementID } from '@shared/models/project.model';
import {
  QuantityUnit,
  SelectableQuantityUnit,
  selectableQuantityUnits,
} from '@shared/models/unit.model';
import NodonSelect from '@ui/NodonSelect';
import NodonTextField from '@ui/NodonTextField';
import { isEqual, pick } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React, {
  ChangeEvent,
  KeyboardEventHandler,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import { useOnClickOutside } from 'usehooks-ts';
import { createWithEqualityFn } from 'zustand/traditional';

import ExpressionSelect, {
  useExpressionSelect,
} from './components/ExpressionSelect';
import { IconButton } from '@ui/buttons';
import { guards } from '@shared/helpers/typeguards.helpers';
import { type ExpressionInputChangeEvent } from '../ExpressionInput/expression-input-change.helpers';
import {
  buildPanelChangeEvent,
  expressionValueAfterClearingPanelField,
} from './ExpressionInputPanel.helpers';

const EXPRESSION_INPUT_PANEL_INPUT_ID = 'expression-input-panel-input';
const EXPRESSION_INPUT_PANEL_CONTAINER = 'expression-input-panel-container';

export type IExpressionInputPanelOutput = {
  expressionValue?: ExpressionValue;
  unit?: SelectableQuantityUnit;
};

type ExpressionInputPanelReturnValue =
  | {
      type: 'save';
      result: IExpressionInputPanelOutput;
    }
  | {
      type: 'cancel';
      result?: undefined;
    };

export type IExpressionInputOptions = {
  /** referenceId can be provided to the input panel so consumers can check if they were the one who opened it */
  referenceId: string;
  expressionValue?: ExpressionValue;
  fallbackExpressionValue?: ExpressionValue;
  min?: number;
  max?: number;
  unit?: QuantityUnit;
  /**
   * Pre-resolved variables. Use this when the consumer already has variables in
   * scope (e.g. {@link ExpressionInput} which needs them for inline validation).
   * For consumers that don't already have variables (e.g. list rows), prefer
   * {@link IExpressionInputOptions.elementId} so the panel can resolve them lazily
   * on open instead of every parent render.
   */
  variables?: ExpressionVariables;
  /**
   * Element id to resolve variables for. The panel resolves variables internally
   * via {@link useElementExpressionVariablesById} only while it's open, avoiding
   * eager subscriptions in every list row. Takes precedence over `variables`.
   */
  elementId?: IElementID;
  disableUnits?: boolean;
  selectableUnits?: SelectableQuantityUnit[];
  onChange?: (event: ExpressionInputChangeEvent) => void;
};
export type ExpressionInputPanelOpenFn = (
  options: IExpressionInputOptions,
) => Promise<ExpressionInputPanelReturnValue>;

type PanelStateOpen = {
  isOpen: true;
  error: Error | null;
  options: IExpressionInputOptions;
  resolve: (value: ExpressionInputPanelReturnValue) => void;
};
type PanelStateClosed = {
  isOpen: false;
  options: undefined;
  error: null;
  resolve: () => void;
};
type PanelState = PanelStateOpen | PanelStateClosed;

const CLOSED_STATE: PanelState = {
  isOpen: false,
  options: undefined,
  error: null,
  resolve: () => {},
};

type ExpressionPanelStore = {
  panelState: PanelState;
  setPanelState: (state: PanelState) => void;
};

const useExpressionPanelStore = createWithEqualityFn<ExpressionPanelStore>()(
  (set) => ({
    panelState: CLOSED_STATE,
    setPanelState: (panelState) => set({ panelState }),
  }),
);

/** Use this to report input while editing. ONLY IN EVENT HANDLERS. */
const setPanelStateError = (error: Error | null) => {
  useExpressionPanelStore.setState((state) => {
    if (state.panelState.error === error) return state;
    if (!state.panelState.isOpen) return state;
    return { panelState: { ...state.panelState, error } };
  });
};

/**
 * External API for expression panel.
 * Provides ways to open, close, check open state and check if the current open panel is mine (using referenceId).
 */
export const expressionPanel = Object.freeze({
  /** for use in components that needs to check if they are the one who opened the panel. */
  useIsMyReferenceId: (myId: string): boolean => {
    const selector = useCallback(
      ({ panelState: { isOpen, options } }: ExpressionPanelStore) =>
        isOpen && options.referenceId === myId,
      [myId],
    );
    return useExpressionPanelStore(selector);
  },
  /** for use in components that needs to listen to open state. */
  useIsOpen: (): boolean => {
    const selector = useCallback(
      ({ panelState }: ExpressionPanelStore) => panelState.isOpen,
      [],
    );
    return useExpressionPanelStore(selector);
  },
  /** for use in event handlers. Will return the current open state */
  isOpen: (): boolean => useExpressionPanelStore.getState().panelState.isOpen,
  /** for use in components that needs to open the panel. can be used in event handlers. */
  open: async (
    options: IExpressionInputOptions,
  ): Promise<ExpressionInputPanelReturnValue> => {
    const { panelState, setPanelState } = useExpressionPanelStore.getState();
    if (panelState.error) {
      throw new Error(
        'Expression panel error must be resolved before opening a new expression.',
      );
    }
    const promise = new Promise<ExpressionInputPanelReturnValue>((resolve) => {
      setPanelState({
        isOpen: true,
        options,
        resolve,
        error: null,
      });
    });

    await sleep(100);

    return promise;
  },
  /** for use in components that needs to open the panel. can be used in event handlers. */
  close: (): void => {
    const { panelState, setPanelState } = useExpressionPanelStore.getState();
    if (panelState.error) return; // don't allow closing the panel if there is an error

    setPanelState(CLOSED_STATE);
  },
});

const useExpression = (
  value: number | undefined = undefined,
  defaultExpression: string,
  options: IExpressionInputOptions,
) => {
  const solveOptions = useObjectMemo(
    pick<ISolveOptions>(options, ['min', 'max']),
  );
  type ExpressionState =
    | { expression: string; expressionValue: ExpressionValue; error: null }
    | { expression: string; expressionValue: null; error: Error };

  const getExpressionState = useCallback(
    (
      /* expression to solve */
      expression: string,
      /* if true, will set panel state error if there is an error. ONLY use in event callbacks, or React will throw a fit */
      reportError: boolean,
    ): ExpressionState => {
      try {
        // expressionInputPanel doesn't handle number values, so it will always be undefined.
        const v = fixNumberFormatSolve(
          value,
          expression,
          options.variables,
          solveOptions,
        );
        if (reportError) setPanelStateError(null);
        return { expression, expressionValue: v, error: null };
      } catch (e) {
        const error = e instanceof Error ? e : new Error(String(e));
        if (reportError) setPanelStateError(error);
        return { expression, expressionValue: null, error };
      }
    },
    [options.variables, solveOptions, value],
  );
  const [state, setState] = useState<ExpressionState>(() =>
    getExpressionState(defaultExpression, false),
  );

  const setExpression = useCallback(
    (expression: string): ExpressionValue | null => {
      const newState = getExpressionState(expression, true);
      setState(newState);
      return newState.expressionValue;
    },
    [getExpressionState],
  );

  return Object.freeze({
    ...state,
    setExpression,
  });
};

/**
 * Retains the last value until a condition is met. Useful for keeping UI state
 * during transitions or when you need to display the previous valid state while
 * the current state is invalid/transitioning.
 *
 * @param current The current value that might change
 * @param updateWhen Function that determines when to update the retained value with the current value
 * @returns The retained value - either the current value (if updateWhen returns true) or the last retained value
 *
 * @example
 * // Keep the last open panel state while panel is closing
 * const lastOpenPanelState = useRetained(panelState, (next) => next.isOpen);
 */
const useRetained = <T,>(current: T, updateWhen: (next: T) => boolean): T => {
  const ref = useRef<T>(current);
  if (updateWhen(current)) ref.current = current;
  return ref.current;
};

const ExpressionInputPanelWrapper: React.FC = () => {
  const styles = useStyles();
  const readonly = useIsReadonly();
  const { panelState, setPanelState } = useExpressionPanelStore();

  // Retain the last open panel state so we can display it when the panel is closing.
  const lastOpenPanelState = useRetained(panelState, (next) => next.isOpen);
  const onSave = useMemo(() => {
    if (!panelState.isOpen) return () => undefined;
    return (result: IExpressionInputPanelOutput) => {
      if (
        guards.is.defined(result.expressionValue?.value) &&
        !isEqual(result.expressionValue, panelState.options.expressionValue)
      ) {
        enqueueSnackbar({
          message:
            'Formula is saved, but will not be applied until the manual value is removed.',
          variant: 'info',
          autoHideDuration: 5000,
        });
      }
      panelState.resolve({ result, type: 'save' });
      setPanelState(CLOSED_STATE);
    };
  }, [panelState, setPanelState]);
  const onCancel = useCallback(() => {
    panelState.resolve({ type: 'cancel' });
    setPanelState(CLOSED_STATE);
  }, [panelState, setPanelState]);

  return (
    <Drawer
      tabIndex={0}
      classes={{
        paper: styles.classes.paper,
      }}
      anchor={'bottom'}
      variant="persistent"
      open={panelState.isOpen && !readonly}
      className={EXPRESSION_INPUT_PANEL_CONTAINER}
    >
      {lastOpenPanelState.isOpen && (
        <ExpressionInputPanelComponent
          key={lastOpenPanelState.options.referenceId} // ensure we render a new component when referenceId changes
          isOpen={panelState.isOpen} // send the true openState, not always the last true
          options={lastOpenPanelState.options}
          onSave={onSave}
          onCancel={onCancel}
        />
      )}
    </Drawer>
  );
};

const ExpressionInputPanelComponent: React.FC<{
  isOpen: boolean;
  options: PanelStateOpen['options'];
  onSave: (state: IExpressionInputPanelOutput) => void;
  onCancel: () => void;
}> = ({ isOpen, options: rawOptions, onSave, onCancel }) => {
  const { classes } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | undefined>(undefined);

  // Resolve variables lazily here so non-panel consumers (e.g. list rows) don't
  // need to subscribe to project state on every render just to support a panel
  // that's only sometimes opened. When `elementId` is provided it takes precedence
  // over any pre-resolved `variables` passed in.
  const elementVariables = useElementExpressionVariablesById(
    rawOptions.elementId,
  );
  const options = useMemo<IExpressionInputOptions>(
    () =>
      rawOptions.elementId
        ? { ...rawOptions, variables: elementVariables }
        : rawOptions,
    [rawOptions, elementVariables],
  );

  const units = options?.selectableUnits ?? selectableQuantityUnits;
  const placeholder = options?.fallbackExpressionValue?.expression ?? '0';

  const {
    expressionValue,
    error,
    expression: localExpression,
    setExpression: setLocalExpression,
  } = useExpression(
    options.expressionValue?.value,
    options.expressionValue?.expression ?? '',
    options,
  );

  const handleSave = useCallback(
    (value: IExpressionInputPanelOutput | undefined, error: Error | null) => {
      if (error) {
        enqueueSnackbar(
          `Cannot close expression input panel.\nEvaluation error: ${error.message}`,
          snackbarOptions,
        );
      } else if (value) {
        onSave(value);
      } else {
        onCancel();
      }
    },
    [onSave, onCancel],
  );

  // should avoid usehooks-ts going forward. Maintainer seems to be inactive, and types are not up to date.
  useOnClickOutside(containerRef as RefObject<HTMLElement>, (e) => {
    // only attempt save if the panel is open, the click is outside the panel and not in an overlay
    if (isOpen && is.Element(e.target) && !hasOverlayParent(e.target)) {
      handleSave(returnValue, error);
    }
  });

  const [localUnit, setLocalUnit] = useState<IExpressionInputOptions['unit']>(
    options.unit,
  );

  const [isInitialInput, setIsInitialInput] = useState(false);

  /** Classify a raw panel output into a typed {@link ExpressionInputChangeEvent} against the baseline options. */
  const classifyPanelChange = useCallback(
    (
      newExpressionValue: ExpressionValue | undefined,
      // unitOverride lets onUnitChange pass the new unit before the React state update lands
      unitOverride?: QuantityUnit,
    ): ExpressionInputChangeEvent =>
      buildPanelChangeEvent(
        options.expressionValue,
        options.unit,
        newExpressionValue,
        unitOverride !== undefined ? unitOverride : localUnit,
      ),
    [options.expressionValue, options.unit, localUnit],
  );

  const onExpression = useCallback(
    (expression: string) => {
      const expressionValue = setLocalExpression(expression);
      if (expressionValue) {
        options.onChange?.(classifyPanelChange(expressionValue));
      }
    },
    [setLocalExpression, options, classifyPanelChange],
  );

  const {
    matchingExpressions,
    activeExpressionIndex,
    setActiveExpressionIndex,
    handlePlusMenuClick,
    chooseExpression,
    customOnKeyDown: selectOnKeyDown,
    onKeyUp: selectOnKeyUp,
    variablePreviewContext,
  } = useExpressionSelect(
    inputRef,
    localExpression,
    onExpression,
    options?.variables,
  );

  // Check if expression and unit have changed since opening panel
  const hasChanged = useMemo((): boolean => {
    const original = options.expressionValue;
    const current = expressionValue?.expression;
    return localUnit !== options.unit || current !== original?.expression;
  }, [options, expressionValue, localUnit]);

  // Return an output if expression is valid and properties has changed
  const returnValue = useMemo((): IExpressionInputPanelOutput | undefined => {
    if (error || !hasChanged) {
      return;
    }

    const selectableUnit = isSelectableQuantityUnit(localUnit)
      ? localUnit
      : undefined;

    if (expressionValue.expression === '') {
      // Empty field drops a manual override. A placeholder formula uses fallback.
      const emptyReturnValue: IExpressionInputPanelOutput = {
        expressionValue: expressionValueAfterClearingPanelField(
          options.expressionValue,
          options.fallbackExpressionValue,
        ),
        unit: selectableUnit,
      };
      return emptyReturnValue;
    }

    return {
      expressionValue: expressionValue,
      unit: selectableUnit,
    };
  }, [
    expressionValue,
    error,
    hasChanged,
    options.fallbackExpressionValue,
    options.expressionValue,
    localUnit,
  ]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      const expression = options.expressionValue?.expression;
      // Need input field and expression to set selection
      if (guards.is.undefined(expression)) return;

      const withoutPercent = expression.replace(/%$/, '');

      if (isFinite(Number(withoutPercent))) {
        // Only select parsable plain numbers (including floating point, scientific notation etc.)
        inputRef.current?.setSelectionRange(0, withoutPercent.length);
      }
    }
  }, [isOpen, options.expressionValue?.expression]);

  const onExpressionChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      /** lots of special cases without comments in this handler. Will leave it mostly intact for now. */
      const { value: newExpression } = event.target;
      const trimmed = newExpression.trim();

      if (trimmed === '' && localExpression === '') {
        // nothing to do
        return;
      }

      if (localUnit === '%') {
        const isPositiveNumber = Number(newExpression) >= 0;
        const isNewInput =
          newExpression.length === 1 && localExpression.length === 0;

        setIsInitialInput(isNewInput);

        if (trimmed.startsWith('%')) {
          const expressionValue = setLocalExpression('');
          if (expressionValue) {
            options.onChange?.(classifyPanelChange(expressionValue));
          }
          return;
        }

        if (
          !/[0.%]/.test(newExpression + localExpression) &&
          isPositiveNumber &&
          isNewInput
        ) {
          const expressionValue = setLocalExpression(newExpression + '%');
          if (expressionValue) {
            options.onChange?.(classifyPanelChange(expressionValue));
          }

          return;
        }
      }

      const expressionValue = setLocalExpression(newExpression);
      if (expressionValue) {
        options.onChange?.(classifyPanelChange(expressionValue));
      }
    },
    [
      localUnit,
      options,
      setLocalExpression,
      localExpression,
      classifyPanelChange,
    ],
  );

  const onUnitChange = useCallback(
    (value: string): void => {
      if (!isSelectableQuantityUnit(value)) return;
      setLocalUnit(value);

      options.onChange?.(
        classifyPanelChange(expressionValue ?? undefined, value),
      );

      inputRef.current?.focus();
    },
    [inputRef, options, expressionValue, classifyPanelChange],
  );

  const onScroll = useCallback(() => {
    /** not sure this does anything at all. Couldn't get this to log anything. */
    setActiveExpressionIndex(undefined);
  }, [setActiveExpressionIndex]);

  const onKeyUp: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.key === KeyboardKeys.ESCAPE) {
        onCancel();
      } else {
        selectOnKeyUp(event);
      }
    },
    [onCancel, selectOnKeyUp],
  );

  const fallbackExpression = options?.fallbackExpressionValue?.expression;
  const onKeyDown: KeyboardEventHandler<
    HTMLInputElement | HTMLButtonElement | HTMLTextAreaElement
  > = useCallback(
    (event) => {
      if (event.key === KeyboardKeys.ENTER) {
        event.preventDefault(); // otherwise possible that confirmation event is triggered in confirm dialog
        handleSave(returnValue, error);
        return;
      }
      // ArrowRight in an empty field pulls the default expression into the
      // input so the user can edit it further instead of retyping the auto
      // expression. Tab still moves focus. Existing text is left alone so the
      // caret can step.
      if (
        shouldFillEmptyFieldOnArrowRight(event, localExpression) &&
        fallbackExpression
      ) {
        event.preventDefault();
        onExpression(fallbackExpression);
      }
    },
    [
      handleSave,
      returnValue,
      error,
      localExpression,
      fallbackExpression,
      onExpression,
    ],
  );

  const slotProps = useMemo(
    () => ({
      // The panel has no visible label, so name the field after what it edits
      htmlInput: { 'aria-label': 'Formula' },
      input: {
        endAdornment: localUnit && (
          <>
            <IconButton
              category="mui"
              icon="AddCircleOutline"
              aria-label="expression menu button"
              onClick={handlePlusMenuClick}
              onKeyDown={selectOnKeyDown}
            />
            {localUnit !== 'none' && (
              <NodonSelect
                buttonLabel={localUnit}
                options={units}
                formatOptionLabels={false}
                disabled={options?.disableUnits}
                onChange={onUnitChange}
              />
            )}
          </>
        ),
        onKeyDown: matchingExpressions.length > 0 ? selectOnKeyDown : onKeyDown,
      },
    }),
    [
      onKeyDown,
      handlePlusMenuClick,
      localUnit,
      matchingExpressions.length,
      onUnitChange,
      options?.disableUnits,
      selectOnKeyDown,
      units,
    ],
  );

  // Place caret before percentage sign if unit is percentage
  useEffect(() => {
    if (localUnit === '%') {
      const percentageIndex = localExpression.indexOf('%');

      if (percentageIndex > 0 && isInitialInput) {
        inputRef.current?.setSelectionRange(percentageIndex, percentageIndex);
      }
    }
  }, [isInitialInput, localExpression, localUnit]);
  return (
    <Box className={classes.root} ref={containerRef}>
      <NodonTextField
        id={EXPRESSION_INPUT_PANEL_INPUT_ID}
        inputRef={inputRef}
        autoComplete="off"
        value={localExpression}
        placeholder={placeholder}
        error={!!error}
        spellCheck={false}
        sx={INPUT_STYLE}
        slotProps={slotProps}
        onKeyUp={onKeyUp}
        onChange={onExpressionChange}
      />

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="text"
          disabled={!hasChanged || guards.is.error(error)}
          onClick={() => handleSave(returnValue, error)}
        >
          Save
        </Button>
      </Box>
      <ExpressionSelect
        matchingExpressions={matchingExpressions}
        activeExpressionIndex={activeExpressionIndex}
        onScroll={onScroll}
        onClick={chooseExpression}
        variables={options?.variables}
        variablePreviewContext={variablePreviewContext}
      />
    </Box>
  );
};

const INPUT_STYLE = { width: '100%' } as const;

const snackbarOptions = {
  variant: 'error',
  autoHideDuration: 5000,
  /**
   * Move snackbar when expression panel is open
   */
  style: {
    height: 40,
    bottom: 105,
    position: 'relative',
    marginLeft: -12,
    padding: '0 8px',
  },
} as const;

const useStyles = makeStyles()(({ spacing, palette }) => ({
  root: {
    padding: spacing(2),
    backgroundColor: 'white',
    borderTop: '1px solid' + palette.primary.light,
  },

  paper: {
    backgroundColor: 'unset',
    border: 'none',
    overflow: 'visible',
    width: `calc(100% - ${SidePanelStyles.WIDTH}px)`,
  },
}));

const ExpressionInputPanel = ExpressionInputPanelWrapper;
export default ExpressionInputPanel;
